/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState } from 'react';
import { useApp } from '../context/AppContext';
import { MCQPulseLogo } from './MCQPulseLogo';
import { InstallAppButton } from './InstallAppButton';
import { 
  LogOut, 
  Menu, 
  X, 
  User, 
  Settings, 
  Trophy, 
  BookOpen, 
  PlusCircle, 
  ShieldAlert,
  Brain,
  MessageCircle,
  HelpCircle,
  LayoutDashboard,
  LineChart,
  CreditCard,
  Key,
  Shield,
  ShoppingCart
} from 'lucide-react';
import { AdminChangePasswordModal } from './admin/AdminChangePasswordModal';

export const Header: React.FC = () => {
  const { 
    user, 
    activePage, 
    setPage, 
    logout, 
    setAuthModalOpen,
    setDashboardTab,
    setAdminWorkspace,
    cart
  } = useApp();

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);

  const handleNav = (page: string, params?: any) => {
    if (page === 'practice') {
      window.dispatchEvent(new CustomEvent('reset_practice_hub'));
    }
    if (page === 'study-material') {
      localStorage.removeItem('mp_selected_subject_id');
      window.dispatchEvent(new CustomEvent('reset_study_material_library'));
    }
    setPage(page, params);
    setMobileMenuOpen(false);
  };

  return (
    <header id="main-app-header" className="sticky top-0 z-50 w-full transition-all duration-300 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 shadow-sm">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          
          {/* Logo */}
          <div className="flex items-center gap-2 sm:gap-2.5 cursor-pointer group shrink-0 min-w-0" onClick={() => handleNav(user && user.role === 'student' ? 'today-aim' : 'home')}>
            <MCQPulseLogo size={42} className="w-10 h-10 sm:w-12 sm:h-12 group-hover:scale-105 transition-transform duration-300 shrink-0" />
            <div className="flex flex-col justify-center">
              <span className="font-display text-xl sm:text-3xl md:text-4xl font-extrabold tracking-tight bg-gradient-to-r from-sky-500 to-indigo-600 bg-clip-text text-transparent whitespace-nowrap flex items-center">
                MCQ PULSE<span className="text-[9px] sm:text-xs text-indigo-500 select-none font-black ml-0.5 self-start -mt-0.5 sm:mt-0.5">™</span>
              </span>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6">
            {(!user || user.role !== 'student') ? (
              <>
                <button 
                  onClick={() => handleNav('home')} 
                  className={`text-sm font-medium transition-colors ${activePage === 'home' ? 'text-indigo-600 dark:text-indigo-400 font-semibold' : 'text-slate-600 hover:text-indigo-500 dark:text-slate-300 dark:hover:text-indigo-400'}`}
                >
                  Home
                </button>
                <button 
                  onClick={() => handleNav('courses')} 
                  className={`text-sm font-medium transition-colors ${activePage === 'courses' || activePage === 'course-details' ? 'text-indigo-600 dark:text-indigo-400 font-semibold' : 'text-slate-600 hover:text-indigo-500 dark:text-slate-300 dark:hover:text-indigo-400'}`}
                >
                  Courses
                </button>
                <button 
                  onClick={() => handleNav('practice')} 
                  className={`text-sm font-medium transition-colors ${activePage === 'practice' || activePage === 'quiz-player' ? 'text-indigo-600 dark:text-indigo-400 font-semibold' : 'text-slate-600 hover:text-indigo-500 dark:text-slate-300 dark:hover:text-indigo-400'}`}
                >
                  Practise Hub
                </button>
                <button 
                  onClick={() => handleNav('leaderboard')} 
                  className={`text-sm font-medium transition-colors ${activePage === 'leaderboard' ? 'text-indigo-600 dark:text-indigo-400 font-semibold' : 'text-slate-600 hover:text-indigo-500 dark:text-slate-300 dark:hover:text-indigo-400'}`}
                >
                  Leaderboard
                </button>
              </>
            ) : (
              <button 
                onClick={() => handleNav('today-aim')} 
                className={`text-sm font-bold tracking-wide uppercase transition-colors ${activePage === 'today-aim' ? 'text-indigo-600 dark:text-indigo-400 font-extrabold' : 'text-slate-600 hover:text-indigo-500 dark:text-slate-300 dark:hover:text-indigo-400'}`}
              >
                MY LEARNING HUB
              </button>
            )}
          </nav>

          {/* Right Icons Section */}
          <div className="flex items-center gap-3">

            <InstallAppButton variant="header-button" />

            {/* User Profile Dropdown Menu */}
            {user ? (
              <div className="flex items-center gap-2">
                {/* Cart Icon Button - strictly displayed beside Student Dashboard only when a student is logged in and has items in cart */}
                {user.role === 'student' && cart && cart.length > 0 && (
                  <button 
                    id="header-cart-button"
                    onClick={() => handleNav('cart')}
                    className="relative flex items-center justify-center p-2 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold shadow-md hover:shadow-lg transition-all hover:scale-105 cursor-pointer"
                    title={`View Cart (${cart.length} item${cart.length > 1 ? 's' : ''})`}
                    aria-label="View Shopping Cart"
                  >
                    <ShoppingCart className="h-4 w-4" />
                    <span className="absolute -top-1.5 -right-1.5 bg-rose-600 text-white text-[10px] font-black rounded-full h-4 min-w-4 px-1 flex items-center justify-center shadow-sm">
                      {cart.length}
                    </span>
                  </button>
                )}

                {/* Dashboard Nav button */}
                <button 
                  onClick={() => handleNav(
                    user.role === 'student' ? 'dashboard' : 'admin-dashboard'
                  )}
                  className="hidden sm:flex items-center gap-1 text-xs font-bold bg-indigo-600 hover:bg-indigo-500 text-white px-3 py-1.5 rounded-lg transition-all"
                >
                  <Trophy className="h-3.5 w-3.5" />
                  <span>Dashboard</span>
                </button>

                {/* Interactive Dropdown */}
                <div className="relative">
                  <button 
                    id="user-profile-menu-button"
                    onClick={() => {
                      setShowProfileDropdown(!showProfileDropdown);
                    }}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800/80 text-slate-700 dark:text-slate-200 transition-all font-semibold text-xs sm:text-sm border border-transparent hover:border-slate-200 dark:hover:border-slate-800"
                    title="User Profile Menu"
                  >
                    {user.photoURL ? (
                      <img src={user.photoURL} alt={user.displayName || (user as any).display_name || 'User'} className="h-5 w-5 rounded-full border border-indigo-400 object-cover" />
                    ) : (
                      <span className="h-5 w-5 rounded-full bg-indigo-50 dark:bg-indigo-950/40 flex items-center justify-center text-[10px] font-bold text-indigo-600 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-800">
                        {(user.displayName || (user as any).display_name || user.email || 'A').charAt(0)}
                      </span>
                    )}
                    <span className="max-w-[120px] truncate">👤 {user.displayName || (user as any).display_name || 'Admin'}</span>
                    <span className="text-slate-400 dark:text-slate-500 text-[10px] scale-75 transition-transform duration-200">▼</span>
                  </button>

                  {showProfileDropdown && (
                    <div id="user-profile-menu-dropdown" className="absolute right-0 mt-2 w-52 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-xl z-50 overflow-hidden divide-y divide-slate-100 dark:divide-slate-800/60 animate-in fade-in slide-in-from-top-2 duration-150">
                      <div className="px-4 py-3 bg-slate-50 dark:bg-slate-800">
                        <p className="text-xs font-bold text-slate-800 dark:text-slate-100 truncate">{user.displayName || (user as any).display_name || 'Admin'}</p>
                        <p className="text-[10px] text-slate-400 dark:text-slate-500 truncate mt-0.5">{user.email}</p>
                      </div>
                      
                      {user.role === 'admin' ? (
                        <div className="py-1">
                          {/* 1. My Profile */}
                          <button 
                            onClick={() => {
                              handleNav('admin-dashboard');
                              setAdminWorkspace('admin_management');
                              setShowProfileDropdown(false);
                            }}
                            className="group flex w-full items-center gap-2.5 px-4 py-2 text-left text-xs font-semibold text-slate-700 dark:text-slate-200 hover:bg-indigo-600 hover:text-white dark:hover:bg-indigo-600 dark:hover:text-white transition-all duration-150"
                          >
                            <User className="h-4 w-4 text-slate-400 group-hover:text-white transition-colors" />
                            <span>My Profile</span>
                          </button>

                          {/* 2. Change Password */}
                          <button 
                            onClick={() => {
                              setShowProfileDropdown(false);
                              setIsChangePasswordOpen(true);
                            }}
                            className="group flex w-full items-center gap-2.5 px-4 py-2 text-left text-xs font-semibold text-slate-700 dark:text-slate-200 hover:bg-indigo-600 hover:text-white dark:hover:bg-indigo-600 dark:hover:text-white transition-all duration-150"
                          >
                            <Key className="h-4 w-4 text-slate-400 group-hover:text-white transition-colors" />
                            <span>Change Password</span>
                          </button>

                          {/* 3. Security */}
                          <button 
                            onClick={() => {
                              handleNav('admin-dashboard');
                              setAdminWorkspace('security');
                              setShowProfileDropdown(false);
                            }}
                            className="group flex w-full items-center gap-2.5 px-4 py-2 text-left text-xs font-semibold text-slate-700 dark:text-slate-200 hover:bg-indigo-600 hover:text-white dark:hover:bg-indigo-600 dark:hover:text-white transition-all duration-150"
                          >
                            <Shield className="h-4 w-4 text-slate-400 group-hover:text-white transition-colors" />
                            <span>Security</span>
                          </button>
                        </div>
                      ) : (
                        <div className="py-1">
                          {/* 1. Dashboard */}
                          <button 
                            onClick={() => {
                              handleNav('dashboard');
                              setDashboardTab('courses');
                              setShowProfileDropdown(false);
                            }}
                            className="group flex w-full items-center gap-2.5 px-4 py-2 text-left text-xs font-semibold text-slate-700 dark:text-slate-200 hover:bg-indigo-600 hover:text-white dark:hover:bg-indigo-600 dark:hover:text-white transition-all duration-150"
                          >
                            <LayoutDashboard className="h-4 w-4 text-slate-400 group-hover:text-white transition-colors" />
                            <span>Dashboard</span>
                          </button>

                          {/* 2. My Courses */}
                          <button 
                            onClick={() => {
                              handleNav('dashboard');
                              setDashboardTab('courses');
                              setShowProfileDropdown(false);
                            }}
                            className="group flex w-full items-center gap-2.5 px-4 py-2 text-left text-xs font-semibold text-slate-700 dark:text-slate-200 hover:bg-indigo-600 hover:text-white dark:hover:bg-indigo-600 dark:hover:text-white transition-all duration-150"
                          >
                            <BookOpen className="h-4 w-4 text-slate-400 group-hover:text-white transition-colors" />
                            <span>My Courses</span>
                          </button>

                          {/* 3. My Tests */}
                          <button 
                            onClick={() => {
                              handleNav('dashboard');
                              setDashboardTab('my-learning');
                              setShowProfileDropdown(false);
                            }}
                            className="group flex w-full items-center gap-2.5 px-4 py-2 text-left text-xs font-semibold text-slate-700 dark:text-slate-200 hover:bg-indigo-600 hover:text-white dark:hover:bg-indigo-600 dark:hover:text-white transition-all duration-150"
                          >
                            <Brain className="h-4 w-4 text-slate-400 group-hover:text-white transition-colors" />
                            <span>My Tests</span>
                          </button>

                          {/* 4. Performance */}
                          <button 
                            onClick={() => {
                              handleNav('dashboard');
                              setDashboardTab('my-learning');
                              setShowProfileDropdown(false);
                            }}
                            className="group flex w-full items-center gap-2.5 px-4 py-2 text-left text-xs font-semibold text-slate-700 dark:text-slate-200 hover:bg-indigo-600 hover:text-white dark:hover:bg-indigo-600 dark:hover:text-white transition-all duration-150"
                          >
                            <LineChart className="h-4 w-4 text-slate-400 group-hover:text-white transition-colors" />
                            <span>Performance</span>
                          </button>

                          {/* 5. Subscription */}
                          <button 
                            onClick={() => {
                              handleNav('dashboard');
                              setDashboardTab('premium-intelligence');
                              setShowProfileDropdown(false);
                            }}
                            className="group flex w-full items-center gap-2.5 px-4 py-2 text-left text-xs font-semibold text-slate-700 dark:text-slate-200 hover:bg-indigo-600 hover:text-white dark:hover:bg-indigo-600 dark:hover:text-white transition-all duration-150"
                          >
                            <CreditCard className="h-4 w-4 text-slate-400 group-hover:text-white transition-colors" />
                            <span>Subscription</span>
                          </button>

                          {/* 6. Settings */}
                          <button 
                            onClick={() => {
                              handleNav('profile', { profileTab: 'settings' });
                              setShowProfileDropdown(false);
                            }}
                            className="group flex w-full items-center gap-2.5 px-4 py-2 text-left text-xs font-semibold text-slate-700 dark:text-slate-200 hover:bg-indigo-600 hover:text-white dark:hover:bg-indigo-600 dark:hover:text-white transition-all duration-150"
                          >
                            <Settings className="h-4 w-4 text-slate-400 group-hover:text-white transition-colors" />
                            <span>Settings</span>
                          </button>
                        </div>
                      )}

                      {/* 7. Logout */}
                      <div className="py-1">
                        <button 
                          onClick={() => {
                            logout();
                            setShowProfileDropdown(false);
                            handleNav('home');
                          }}
                          className="group flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-xs font-bold text-rose-600 dark:text-rose-400 hover:bg-rose-600 hover:text-white dark:hover:bg-rose-600 dark:hover:text-white transition-all duration-150"
                        >
                          <LogOut className="h-4 w-4 text-rose-400 group-hover:text-white transition-colors" />
                          <span>Logout</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <button 
                onClick={() => setAuthModalOpen(true, 'login')}
                className="text-xs font-bold bg-gradient-to-r from-sky-500 to-indigo-600 text-white px-3 sm:px-4 py-2 rounded-xl hover:opacity-95 shadow-md transition-all hover:scale-[1.02] cursor-pointer whitespace-nowrap shrink-0"
              >
                <span className="sm:hidden">Login</span>
                <span className="hidden sm:inline">Login / Register</span>
              </button>
            )}

            {/* Mobile Menu Icon */}
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300"
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>

          </div>
        </div>
      </div>

      {/* Mobile Navigation Drawer */}
      {mobileMenuOpen && (
        <div id="mobile-nav-drawer" className="md:hidden bg-white dark:bg-slate-900 border-t border-slate-100 dark:border-slate-800 px-4 py-4 space-y-3 shadow-lg overflow-y-auto max-h-[85vh]">
          
          <InstallAppButton variant="card" />

          {(!user || user.role !== 'student') ? (
            <>
              <button 
                onClick={() => handleNav('home')} 
                className="block w-full text-left px-3 py-2 rounded-lg text-sm font-semibold hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                Home
              </button>
              <button 
                onClick={() => handleNav('courses')} 
                className="block w-full text-left px-3 py-2 rounded-lg text-sm font-semibold hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                Courses
              </button>
              <button 
                onClick={() => handleNav('practice')} 
                className="block w-full text-left px-3 py-2 rounded-lg text-sm font-semibold hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                Practise Hub
              </button>
              <button 
                onClick={() => handleNav('leaderboard')} 
                className="block w-full text-left px-3 py-2 rounded-lg text-sm font-semibold hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                Leaderboard
              </button>
            </>
          ) : (
            <button 
              onClick={() => handleNav('today-aim')} 
              className="block w-full text-left px-3 py-2 rounded-lg text-sm font-bold uppercase tracking-wide text-indigo-600 dark:text-indigo-400 hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              MY LEARNING HUB
            </button>
          )}


          {user && user.role === 'student' && cart && cart.length > 0 && (
            <button 
              onClick={() => handleNav('cart')} 
              className="flex items-center justify-between w-full text-left px-3 py-2 rounded-lg text-sm font-bold bg-amber-500/10 text-amber-600 dark:text-amber-400 hover:bg-amber-500/20 border border-amber-500/30"
            >
              <span className="flex items-center gap-2">
                <ShoppingCart className="h-4 w-4" />
                <span>My Shopping Cart</span>
              </span>
              <span className="bg-amber-500 text-slate-950 text-xs px-2 py-0.5 rounded-full font-extrabold">
                {cart.length} item{cart.length > 1 ? 's' : ''}
              </span>
            </button>
          )}

          {user && user.role !== 'admin' && (
            <div className="pt-3 border-t border-slate-200 dark:border-slate-700 space-y-2">
              <button 
                onClick={() => handleNav(user.role === 'student' ? 'dashboard' : 'admin-dashboard')}
                className="w-full text-center bg-sky-100 hover:bg-sky-200 text-blue-800 dark:text-sky-200 border border-sky-300/80 dark:bg-sky-950 dark:hover:bg-sky-900 dark:border-sky-800 font-bold py-2 rounded-lg text-xs transition-colors"
              >
                Go to Dashboard
              </button>
              <button 
                onClick={() => handleNav('profile', { profileTab: 'view' })}
                className="w-full text-center border border-slate-300 dark:border-slate-600 font-semibold py-2 rounded-lg text-xs text-slate-700 dark:text-slate-300"
              >
                My Profile
              </button>
              <button 
                onClick={() => handleNav('profile', { profileTab: 'settings' })}
                className="w-full text-center border border-slate-300 dark:border-slate-600 font-semibold py-2 rounded-lg text-xs text-slate-700 dark:text-slate-300"
              >
                Profile Settings
              </button>
              <button 
                onClick={() => { logout(); setMobileMenuOpen(false); }}
                className="w-full text-center bg-rose-500 hover:bg-rose-600 text-white font-bold py-2 rounded-lg text-xs"
              >
                Log Out
              </button>
            </div>
          )}
          {!user && (
            <div className="pt-3 border-t border-slate-200 dark:border-slate-700">
              <button 
                onClick={() => { setAuthModalOpen(true, 'login'); setMobileMenuOpen(false); }}
                className="w-full text-center bg-gradient-to-r from-sky-500 to-indigo-600 text-white font-bold py-2 rounded-lg text-xs hover:opacity-90 shadow-md transition-all"
              >
                Login / Register
              </button>
            </div>
          )}
        </div>
      )}
      <AdminChangePasswordModal isOpen={isChangePasswordOpen} onClose={() => setIsChangePasswordOpen(false)} />
    </header>
  );
};
